// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import Button from '../components/Button';
import ErrorState from '../components/ErrorState';
import Footer from '../components/Footer';
import Layout from '../components/Layout';
import ProductList from '../components/ProductList';
import * as S from '../styles/Home.styled';
import { useQuery } from '@tanstack/react-query';
import ApiGateway from '../gateways/Api.gateway';
import Banner from '../components/Banner';
import { CypressFields } from '../utils/Cypress';
import { useCurrency } from '../providers/Currency.provider';
import StargazingCountdown from '../components/StargazingCountdown';
import { badgeSlug, getProductMeta, productBadges } from '../utils/ProductMeta';

const categoryOrder = ['telescopes', 'binoculars', 'solar', 'accessories', 'books'];

const showcaseProductId = '66VCHSJNUP';
const showcaseMeta = getProductMeta(showcaseProductId);

const marqueeItems = [
  'See further tonight',
  'Free shipping over $50',
  'Field-tested gear',
  'Tracked from cart to doorstep',
  '30-day returns',
  'Clear skies guaranteed',
];


const Home: NextPage = () => {
  const router = useRouter();
  const { selectedCurrency } = useCurrency();
  const activeCategory = typeof router.query.category === 'string' ? router.query.category : '';
  const activeTag = typeof router.query.tag === 'string' ? router.query.tag : '';
  const {
    data: productList = [],
    isError: isProductListError,
    refetch: refetchProductList,
  } = useQuery({
    queryKey: ['products', selectedCurrency],
    queryFn: () => ApiGateway.listProducts(selectedCurrency)
  });

  // react-query keeps the cached list when a refetch fails, so only take over
  // the page when there is genuinely nothing to show. Otherwise a failed
  // background refetch (window focus, currency change) would replace a working
  // grid with an error.
  const showProductListError = isProductListError && productList.length === 0;

  const setFilter = (updates: { category?: string; tag?: string }) => {
    const query = { ...router.query };
    for (const [key, value] of Object.entries(updates)) {
      delete query[key];
      if (value) {
        query[key] = value;
      }
    }
    const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    router.replace(
      { pathname: router.pathname, query, ...(hash ? { hash } : {}) },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const setActiveCategory = (category: string) => setFilter({ category, tag: '' });
  const setActiveTag = (tag: string) => setFilter({ tag });

  const categories = useMemo(() => {
    const available = new Set(productList.flatMap(product => product.categories ?? []));
    const ordered = categoryOrder.filter(category => available.has(category));
    const extras = [...available].filter(category => !categoryOrder.includes(category)).sort();
    return [...ordered, ...extras];
  }, [productList]);

  const categoryProducts = useMemo(() => {
    if (!activeCategory) {
      return productList;
    }
    return productList.filter(product => (product.categories ?? []).includes(activeCategory));
  }, [productList, activeCategory]);

  const availableTags = useMemo(() => {
    const present = new Set(categoryProducts.map(product => getProductMeta(product.id).badge).filter(Boolean));
    return productBadges.filter(tag => present.has(tag));
  }, [categoryProducts]);

  const visibleProducts = useMemo(() => {
    if (!activeTag) {
      return categoryProducts;
    }
    return categoryProducts.filter(product => badgeSlug(getProductMeta(product.id).badge) === activeTag);
  }, [categoryProducts, activeTag]);


  return (
    <Layout>
      <S.Home data-cy={CypressFields.HomePage}>
        <Banner />
        <S.Marquee aria-hidden="true">
          <S.MarqueeTrack>
            {[...marqueeItems, ...marqueeItems].map((item, index) => (
              <S.MarqueeItem key={index}>{item}</S.MarqueeItem>
            ))}
          </S.MarqueeTrack>
        </S.Marquee>
        <S.LogoStrip>
          <S.LogoStripInner>
            <S.LogoStripLabel>As seen in</S.LogoStripLabel>
            <S.LogoRow>
              <S.LogoWordmark $style="serif">The Stargazer</S.LogoWordmark>
              <S.LogoWordmark $style="wide">AstroWeekly</S.LogoWordmark>
              <S.LogoWordmark $style="italic">Night Sky Journal</S.LogoWordmark>
              <S.LogoWordmark $style="black">ORBIT</S.LogoWordmark>
              <S.LogoWordmark $style="mono">deep_field</S.LogoWordmark>
            </S.LogoRow>
          </S.LogoStripInner>
        </S.LogoStrip>
        <S.Container>
          <S.Row>
            <S.Content>
              <S.HotProducts id="shop">
                <S.SectionHead>
                  <S.HotProductsTitle data-cy={CypressFields.HotProducts} id="hot-products">
                    Hot Products
                  </S.HotProductsTitle>
                  <S.HeadControls>
                    {availableTags.length > 0 && (
                      <S.FilterSelectWrap>
                        <S.FilterSelect
                          value={activeTag}
                          onChange={event => setActiveTag(event.target.value)}
                          aria-label="Filter products"
                        >
                          <option value="">All products</option>
                          {availableTags.map(tag => (
                            <option key={tag} value={badgeSlug(tag)}>
                              {tag}
                            </option>
                          ))}
                        </S.FilterSelect>
                        <S.FilterArrow />
                      </S.FilterSelectWrap>
                    )}
                    {!showProductListError && (
                      <S.ProductCount>
                        {visibleProducts.length} {visibleProducts.length === 1 ? 'product' : 'products'}
                      </S.ProductCount>
                    )}
                  </S.HeadControls>
                </S.SectionHead>
                {showProductListError ? (
                  <ErrorState
                    title="We could not load products"
                    message="Something went wrong on our end. Try again in a moment."
                    onRetry={refetchProductList}
                  />
                ) : (
                  <>
                    <S.Chips>
                      <S.Chip $active={!activeCategory} onClick={() => setActiveCategory('')}>
                        All
                      </S.Chip>
                      {categories.map(category => (
                        <S.Chip
                          key={category}
                          $active={activeCategory === category}
                          onClick={() => setActiveCategory(category)}
                        >
                          {category}
                        </S.Chip>
                      ))}
                    </S.Chips>
                    <ProductList productList={visibleProducts} />
                  </>
                )}
              </S.HotProducts>
            </S.Content>
          </S.Row>
        </S.Container>
        <StargazingCountdown />
        <S.Showcase>
          <S.ShowcaseImage>
            <img src="/images/products/StarsenseExplorer.jpg" alt="Starsense Explorer Refractor Telescope" />
            <S.ShowcaseTag>
              <span>★ {showcaseMeta.rating.toFixed(1)}</span> {showcaseMeta.reviewCount} reviews and counting
            </S.ShowcaseTag>
          </S.ShowcaseImage>
          <div>
            <S.ShowcaseEyebrow>The one to beat</S.ShowcaseEyebrow>
            <S.ShowcaseTitle>Meet the Starsense Explorer.</S.ShowcaseTitle>
            <S.ShowcaseCopy>
              Our most-loved refractor pairs crisp 102mm optics with a phone-guided mount that points itself. Line up
              the app, tap a nebula, and it walks you to it.
            </S.ShowcaseCopy>
            <S.ShowcaseList>
              <li>
                <img src="/icons/Check.svg" alt="" /> Phone-guided star finding
              </li>
              <li>
                <img src="/icons/Check.svg" alt="" /> Tool-free setup in under five minutes
              </li>
              <li>
                <img src="/icons/Check.svg" alt="" /> Sharp views of the Moon, planets, and beyond
              </li>
            </S.ShowcaseList>
            <Button as={Link} href={`/product/${showcaseProductId}`}>
              Shop the Starsense
            </Button>
          </div>
        </S.Showcase>
        <S.Comparison>
          <S.ComparisonInner>
            <S.ComparisonTitle>
              Why stargazers <span>switch to us</span>
            </S.ComparisonTitle>
            <S.ComparisonTable>
              <S.ComparisonRow $head>
                <p>What you get</p>
                <p>OpenTelemetry Demo</p>
                <p>Typical store</p>
              </S.ComparisonRow>
              <S.ComparisonRow>
                <p>Star-tested before shipping</p>
                <p>✓</p>
                <p>—</p>
              </S.ComparisonRow>
              <S.ComparisonRow>
                <p>Free shipping over $50</p>
                <p>✓</p>
                <p>—</p>
              </S.ComparisonRow>
              <S.ComparisonRow>
                <p>30-day no-questions returns</p>
                <p>✓</p>
                <p>—</p>
              </S.ComparisonRow>
              <S.ComparisonRow>
                <p>Every order traced end to end</p>
                <p>✓</p>
                <p>—</p>
              </S.ComparisonRow>
              <S.ComparisonRow>
                <p>Stargazer support, day or night</p>
                <p>✓</p>
                <p>—</p>
              </S.ComparisonRow>
            </S.ComparisonTable>
          </S.ComparisonInner>
        </S.Comparison>
        <S.Benefits>
          <S.BenefitsHead>
            <S.BenefitsEyebrow>Why shop with us</S.BenefitsEyebrow>
            <S.BenefitsTitle>The OpenTelemetry Demo difference</S.BenefitsTitle>
          </S.BenefitsHead>
          <S.BenefitsInner>
            <S.BenefitCard>
              <S.BenefitIcon>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2l2.4 5.2 5.6.7-4.1 3.9 1 5.6-4.9-2.7-4.9 2.7 1-5.6L4 7.9l5.6-.7L12 2z" />
              </svg>
              </S.BenefitIcon>
              <h3>Expert-tested gear</h3>
              <p>Every scope is collimated, checked, and star-tested by our team before it ships.</p>
            </S.BenefitCard>
            <S.BenefitCard>
              <S.BenefitIcon>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 7h14v10H1zM15 10h4l4 4v3h-8zM5.5 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM18.5 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
              </svg>
              </S.BenefitIcon>
              <h3>Free shipping over $50</h3>
              <p>Fast, insured delivery. Every order is traced end to end, literally.</p>
            </S.BenefitCard>
            <S.BenefitCard>
              <S.BenefitIcon>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 3-6.7M3 3v5h5" />
              </svg>
              </S.BenefitIcon>
              <h3>30-day returns</h3>
              <p>Cloudy season? Changed your mind? Send it back within 30 days, no questions.</p>
            </S.BenefitCard>
            <S.BenefitCard>
              <S.BenefitIcon>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.6 8.6 0 0 1-3.5-.74L3 21l1.74-6A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
              </svg>
              </S.BenefitIcon>
              <h3>Stargazer support</h3>
              <p>Stuck picking an eyepiece? Our assistant is one click away, day or night.</p>
            </S.BenefitCard>
          </S.BenefitsInner>
        </S.Benefits>
        <S.Testimonials>
          <S.TestimonialsInner>
            <S.TestimonialsTitle>Loved by stargazers</S.TestimonialsTitle>
            <S.TestimonialsSub>
              <span aria-hidden="true">★★★★★</span> 4.8 average across 1,200+ orders
            </S.TestimonialsSub>
            <S.TestimonialGrid>
              <S.TestimonialCard>
                <S.TestimonialHead>
                  <img src="/images/avatars/maya.jpg" alt="" />
                  <figcaption>
                    <strong>Maya R.</strong> First-time stargazer
                  </figcaption>
                  <S.VerifiedBadge>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Verified buyer
                  </S.VerifiedBadge>
                </S.TestimonialHead>
                <blockquote>
                  The Explorascope was my first telescope. Saturn&apos;s rings from my balcony on night one, I actually
                  yelled.
                </blockquote>
                <S.TestimonialStars aria-label="Rated 5 out of 5">★★★★★</S.TestimonialStars>
                <S.TestimonialPurchase>
                  Bought the <strong>National Park Foundation Explorascope</strong>
                </S.TestimonialPurchase>
              </S.TestimonialCard>
              <S.TestimonialCard>
                <S.TestimonialHead>
                  <img src="/images/avatars/dev.jpg" alt="" />
                  <figcaption>
                    <strong>Dev T.</strong> Astrophotographer
                  </figcaption>
                  <S.VerifiedBadge>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Verified buyer
                  </S.VerifiedBadge>
                </S.TestimonialHead>
                <blockquote>
                  Ordered Monday, watched the tracking the whole way, unboxed Thursday. Collimated perfectly out of the
                  box.
                </blockquote>
                <S.TestimonialStars aria-label="Rated 5 out of 5">★★★★★</S.TestimonialStars>
                <S.TestimonialPurchase>
                  Bought the <strong>Starsense Explorer Refractor Telescope</strong>
                </S.TestimonialPurchase>
              </S.TestimonialCard>
              <S.TestimonialCard>
                <S.TestimonialHead>
                  <img src="/images/avatars/sam.jpg" alt="" />
                  <figcaption>
                    <strong>Sam K.</strong> Birder gone stargazer
                  </figcaption>
                  <S.VerifiedBadge>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Verified buyer
                  </S.VerifiedBadge>
                </S.TestimonialHead>
                <blockquote>
                  Their assistant talked me out of overspending and into the right binoculars. Rare honesty in a shop.
                </blockquote>
                <S.TestimonialStars aria-label="Rated 5 out of 5">★★★★★</S.TestimonialStars>
                <S.TestimonialPurchase>
                  Bought the <strong>Roof Binoculars</strong>
                </S.TestimonialPurchase>
              </S.TestimonialCard>
            </S.TestimonialGrid>
          </S.TestimonialsInner>
        </S.Testimonials>
        <S.Faq id="faq">
          <S.FaqTitle>Frequently asked questions</S.FaqTitle>
          <S.FaqItem>
            <S.FaqQuestion>How long does shipping take?</S.FaqQuestion>
            <S.FaqAnswer>
              Orders ship within 24 hours and arrive in 3 to 5 business days. Shipping is free over $50, and you get a
              tracking link the moment your order is confirmed.
            </S.FaqAnswer>
          </S.FaqItem>
          <S.FaqItem>
            <S.FaqQuestion>What is your return policy?</S.FaqQuestion>
            <S.FaqAnswer>
              You have 30 days from delivery to return anything in its original packaging for a full refund. Start a
              return by replying to your confirmation email.
            </S.FaqAnswer>
          </S.FaqItem>
          <S.FaqItem>
            <S.FaqQuestion>Which telescope should I start with?</S.FaqQuestion>
            <S.FaqAnswer>
              For a first scope we usually point people at the National Park Foundation Explorascope: light, sturdy,
              and great on the moon and planets. If you want a personal pick, ask our assistant in the corner.
            </S.FaqAnswer>
          </S.FaqItem>
          <S.FaqItem>
            <S.FaqQuestion>Can I track my order?</S.FaqQuestion>
            <S.FaqAnswer>
              Yes. Every order is traced from cart to doorstep, and we mean every single hop. You will receive a
              shipping tracking ID with your confirmation.
            </S.FaqAnswer>
          </S.FaqItem>
          <S.FaqItem>
            <S.FaqQuestion>Is this a real shop?</S.FaqQuestion>
            <S.FaqAnswer>
              No. This is a demonstration storefront run by Grafana Field Engineering on the OpenTelemetry demo.
              Orders, payments, and shipping are all simulated, so feel free to buy as many telescopes as you like.
            </S.FaqAnswer>
          </S.FaqItem>
        </S.Faq>
        <Footer />
      </S.Home>
    </Layout>
  );
};

export default Home;
